/* ===== Documents: compose a PDF with the official logo and sign it ===== */

import { useState } from 'react'
import type { TenantContext } from '@zetyar/data-contracts'
import type { DocumentCategory, DocumentSignatureMethod } from '@zetyar/domain'
import { Eye } from 'lucide-react'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Modal } from '@/presentation/components/ui/Modal'
import { webDocumentRepository } from '@/infrastructure/repositories/documents'
import { uploadOrganizationDocument } from '@/infrastructure/storage/documents'
import { SignaturePad } from './SignaturePad'
import { buildSignedDocumentPdf, sha256Hex } from './documentPdf'

const FIELD_CLASS =
  'h-11 w-full rounded-[13px] border border-neutral-200 bg-white px-3.5 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100'
const TEXTAREA_CLASS =
  'w-full rounded-[13px] border border-neutral-200 bg-white px-3.5 py-2.5 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100'

const MAX_SIGNATURE_BYTES = 1024 * 1024
const SIGNATURE_IMAGE_TYPES = ['image/png', 'image/jpeg']

interface DocumentComposerModalProps {
  readonly context: TenantContext
  readonly organizationName: string
  readonly logoUrl: string | null
  readonly categories: readonly DocumentCategory[]
  readonly defaultSignerName: string
  readonly defaultSignerPosition: string
  readonly onClose: () => void
  readonly onCreated: () => void
}

function composerErrorMessage(error: unknown): string {
  const message = typeof error === 'object' && error !== null && 'message' in error ? String((error as { message: unknown }).message) : ''
  if (message.includes('documents_forbidden') || message.toLowerCase().includes('row-level security') || message.includes('Unauthorized')) {
    return 'Tu rol no permite crear documentos en esta organización.'
  }
  if (message.includes('documents_invalid_hash') || message.includes('documents_invalid_signature_method')) {
    return 'La firma no se registró correctamente. Vuelve a firmar e intenta de nuevo.'
  }
  return 'No se pudo crear el documento. Intenta nuevamente.'
}

function fileNameFor(title: string): string {
  // Strip combining accent marks by code point: clearer than a regex with literal marks in it.
  const slug = Array.from(title.trim().toLowerCase().normalize('NFD'))
    .filter((character) => {
      const code = character.codePointAt(0) ?? 0
      return code < 0x300 || code > 0x36f
    })
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return `${slug || 'documento'}.pdf`
}

export function DocumentComposerModal({
  context,
  organizationName,
  logoUrl,
  categories,
  defaultSignerName,
  defaultSignerPosition,
  onClose,
  onCreated,
}: DocumentComposerModalProps) {
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [place, setPlace] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [signerName, setSignerName] = useState(defaultSignerName)
  const [signerPosition, setSignerPosition] = useState(defaultSignerPosition)
  const [method, setMethod] = useState<DocumentSignatureMethod>('canvas')
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const pickSignatureImage = (file: File | undefined) => {
    setError(null)
    if (!file) {
      setSignatureDataUrl(null)
      return
    }
    if (!SIGNATURE_IMAGE_TYPES.includes(file.type)) {
      setError('La imagen de la firma debe ser PNG o JPG.')
      return
    }
    if (file.size > MAX_SIGNATURE_BYTES) {
      setError('La imagen de la firma no puede superar 1 MB.')
      return
    }
    const reader = new FileReader()
    reader.onload = () => setSignatureDataUrl(typeof reader.result === 'string' ? reader.result : null)
    reader.onerror = () => setError('No se pudo leer la imagen de la firma.')
    reader.readAsDataURL(file)
  }

  const changeMethod = (next: DocumentSignatureMethod) => {
    setMethod(next)
    setSignatureDataUrl(null)
  }

  const validate = (): string | null => {
    if (!title.trim()) return 'Ingresa un título.'
    if (!body.trim()) return 'Escribe el contenido del documento.'
    if (!signerName.trim()) return 'Indica quién firma.'
    if (!signatureDataUrl) return 'Falta la firma: dibújala o sube una imagen.'
    return null
  }

  const composePdf = async () =>
    await buildSignedDocumentPdf({
      organizationName,
      logoUrl,
      title: title.trim(),
      body,
      place,
      signerName: signerName.trim(),
      signerPosition: signerPosition.trim() || undefined,
      signatureDataUrl: signatureDataUrl as string,
      signedAt: new Date(),
    })

  const preview = async () => {
    const invalid = validate()
    if (invalid) {
      setError(invalid)
      return
    }
    setIsPreviewing(true)
    setError(null)
    try {
      const bytes = await composePdf()
      const url = URL.createObjectURL(new Blob([bytes as unknown as BlobPart], { type: 'application/pdf' }))
      window.open(url, '_blank', 'noopener,noreferrer')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (caught) {
      console.error('Error al generar la vista previa:', caught)
      setError('No se pudo generar la vista previa.')
    } finally {
      setIsPreviewing(false)
    }
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    const createSigned = webDocumentRepository.createSigned
    if (!createSigned || isSaving) return

    const invalid = validate()
    if (invalid) {
      setError(invalid)
      return
    }

    setIsSaving(true)
    setError(null)
    try {
      const bytes = await composePdf()
      const fileName = fileNameFor(title)
      const file = new File([bytes as unknown as BlobPart], fileName, { type: 'application/pdf' })
      // The hash covers the exact bytes that get stored, so a copy can be checked later.
      const fileSha256 = await sha256Hex(bytes)
      const objectPath = await uploadOrganizationDocument({ organizationId: context.organizationId, file })

      await createSigned(context, {
        title: title.trim(),
        fileUrl: objectPath,
        fileName,
        fileSize: file.size,
        categoryId: categoryId || undefined,
        method,
        signerName: signerName.trim(),
        signerPosition: signerPosition.trim() || undefined,
        fileSha256,
      })
      onCreated()
    } catch (caught) {
      console.error('Error al crear el documento firmado:', caught)
      setError(composerErrorMessage(caught))
      setIsSaving(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!isSaving) onClose()
      }}
      title="Crear documento"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button variant="secondary" leftIcon={<Eye className="h-4 w-4" />} isLoading={isPreviewing} onClick={() => void preview()}>
            Vista previa
          </Button>
          <Button type="submit" form="document-composer-form" isLoading={isSaving}>
            Firmar y guardar
          </Button>
        </>
      }
    >
      <form id="document-composer-form" className="space-y-4" onSubmit={submit}>
        <Input label="Título" maxLength={160} value={title} onChange={(event) => setTitle(event.target.value)} />

        <label className="flex flex-col gap-1.5 text-sm font-semibold text-neutral-700 dark:text-neutral-200">
          Contenido
          <textarea
            rows={9}
            maxLength={8000}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            className={TEXTAREA_CLASS}
            placeholder="Escribe el texto del acta, comunicado o certificado."
          />
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Lugar (opcional)" maxLength={80} placeholder="Ej: Santiago" value={place} onChange={(event) => setPlace(event.target.value)} />
          <label className="flex flex-col gap-1.5 text-sm font-semibold text-neutral-700 dark:text-neutral-200">
            Etiqueta
            <select value={categoryId} onChange={(event) => setCategoryId(event.target.value)} className={FIELD_CLASS}>
              <option value="">Sin etiqueta</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </label>
          <Input label="Firma de" maxLength={160} value={signerName} onChange={(event) => setSignerName(event.target.value)} />
          <Input label="Cargo (opcional)" maxLength={120} value={signerPosition} onChange={(event) => setSignerPosition(event.target.value)} />
        </div>

        <fieldset className="rounded-[18px] border border-neutral-200 p-4 dark:border-neutral-800">
          <legend className="px-1 text-sm font-semibold text-neutral-900 dark:text-neutral-100">Firma</legend>
          <div className="mb-3 flex gap-2">
            {(['canvas', 'image'] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={method === value}
                onClick={() => changeMethod(value)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                  method === value
                    ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-200'
                    : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800'
                }`}
              >
                {value === 'canvas' ? 'Dibujar' : 'Subir imagen'}
              </button>
            ))}
          </div>

          {method === 'canvas' ? (
            <SignaturePad onChange={setSignatureDataUrl} />
          ) : (
            <div className="space-y-2">
              <input
                type="file"
                accept={SIGNATURE_IMAGE_TYPES.join(',')}
                onChange={(event) => pickSignatureImage(event.target.files?.[0])}
                className="text-sm text-neutral-600 dark:text-neutral-300"
              />
              <p className="text-xs text-neutral-500 dark:text-neutral-400">PNG o JPG, máximo 1 MB. Idealmente con fondo claro.</p>
              {signatureDataUrl && (
                <img src={signatureDataUrl} alt="Vista previa de la firma" className="h-20 rounded-[13px] border border-neutral-200 bg-white object-contain p-2 dark:border-neutral-700" />
              )}
            </div>
          )}
        </fieldset>

        <p className="rounded-[13px] bg-warning-50 px-3 py-2 text-xs text-warning-700 dark:bg-warning-500/15 dark:text-warning-100">
          Esta es una firma manuscrita digitalizada, equivalente a firma electrónica simple (Ley 19.799). Sirve para
          documentos internos, pero no reemplaza a una firma electrónica avanzada ni a un notario. Se guarda quién firmó,
          cuándo y una huella del archivo para verificarlo después.
        </p>

        {error && <p role="alert" className="text-sm text-danger-600 dark:text-danger-300">{error}</p>}
      </form>
    </Modal>
  )
}
